import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Input, Space, Table, Tag } from 'antd';
import { FilterOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import { getArticleMovements } from '../../models/articleAPI';
import { displayDate, displayNumber } from '../details/RecordDetail';
import './ArticleMovements.css';

const statusLabels = { VALIDE: 'Validé', TRANSFERE: 'Transféré', GROUPE: 'Groupe' };
const stockTag = row => {
  const quantity = row.stock?.qte_stk;
  return <Tag key={row.code_ent} color={quantity == null ? 'default' : quantity < 0 ? 'red' : quantity === 0 ? 'orange' : 'blue'}>
    {row.name || row.code_ent} ({row.code_ent}): {quantity == null ? 'N/A' : displayNumber(quantity)}
  </Tag>;
};

export default function ArticleMovements({ articleId }) {
  const [movements, setMovements] = useState([]);
  const [stocks, setStocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [revision, setRevision] = useState(0);
  const [filters, setFilters] = useState({});
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });

  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    setLoading(true);
    setError('');
    setMovements([]);
    setStocks([]);
    getArticleMovements(articleId, { signal: controller.signal })
      .then(response => {
        if (active) { setMovements(response.data.movements); setStocks(response.data.stocks || []); }
      })
      .catch(err => { if (active) setError(err.response?.data?.message || 'Impossible de charger les mouvements.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; controller.abort(); };
  }, [articleId, revision]);

  useEffect(() => {
    setFilters({}); setPagination({ current: 1, pageSize: 10 });
  }, [articleId]);

  const warehouses = useMemo(() => {
    const names = new Map(stocks.map(row => [row.code_ent, row.name || row.code_ent]));
    movements.forEach(row => { if (row.warehouse && !names.has(row.warehouse)) names.set(row.warehouse, row.warehouse); });
    return [...names].map(([value, name]) => ({ value, text: `${value} - ${name}` }));
  }, [stocks, movements]);
  const stockByWarehouse = useMemo(() => new Map(stocks.map(row => [row.code_ent, row])), [stocks]);
  const visibleStocks = stocks.filter(row => !filters.warehouse?.length || filters.warehouse.includes(row.code_ent));
  const searchColumn = (dataIndex, label, text = row => row[dataIndex]) => ({
    filteredValue: filters[dataIndex] || null,
    filterDropdownProps: { destroyPopupOnHide: true },
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => <div style={{ padding: 8 }}>
      <Input aria-label={`Rechercher ${label}`} placeholder={`Rechercher ${label}…`} value={selectedKeys[0] || ''}
        onChange={event => setSelectedKeys(event.target.value ? [event.target.value] : [])}
        onPressEnter={() => confirm()} style={{ marginBottom: 8, display: 'block' }} />
      <Space>
        <Button type="primary" size="small" icon={<SearchOutlined />} onClick={() => confirm()}>Rechercher</Button>
        <Button size="small" onClick={() => { clearFilters(); confirm(); }}>Réinitialiser</Button>
      </Space>
    </div>,
    filterIcon: filtered => <SearchOutlined aria-label={`Filtrer par ${label}`} style={{ color: filtered ? '#1890ff' : undefined }} />,
    onFilter: (value, row) => String(text(row) || '').toLocaleLowerCase().includes(String(value).trim().toLocaleLowerCase()),
  });
  const columns = [
    { title: 'Date', dataIndex: 'date', width: 115, render: displayDate, sorter: (a, b) => new Date(a.date) - new Date(b.date) },
    { title: 'Mouvement', dataIndex: 'direction', width: 115,
      filters: [{ text: 'Entrée', value: 'entree' }, { text: 'Sortie', value: 'sortie' }], filteredValue: filters.direction || null,
      onFilter: (value, row) => row.direction === value,
      render: direction => <Tag color={direction === 'entree' ? 'green' : 'red'}>{direction === 'entree' ? 'Entrée' : 'Sortie'}</Tag> },
    { title: 'Document', dataIndex: 'reference', width: 175, ...searchColumn('reference', 'document'),
      render: (reference, row) => <><strong>{reference}</strong><small className="article-movement-subtext">{row.source}</small></> },
    { title: 'Statut', dataIndex: 'status', width: 105, render: status => statusLabels[status] || status },
    { title: 'Compte', dataIndex: 'compte', width: 125, ...searchColumn('compte', 'compte'), render: value => value || '—' },
    { title: 'Client / Fournisseur', dataIndex: 'party', width: 230, ...searchColumn('party', 'client ou fournisseur', row => `${row.party} ${row.partyCode}`),
      render: (value, row) => <>{value || '—'}<small className="article-movement-subtext">{row.partyType} · {row.partyCode}</small></> },
    { title: 'Entrepôt', dataIndex: 'warehouse', width: 160, filters: warehouses, filterSearch: true,
      filterIcon: filtered => <FilterOutlined aria-label="Filtrer par entrepôt" style={{ color: filtered ? '#1890ff' : undefined }} />,
      filteredValue: filters.warehouse || null, onFilter: (value, row) => row.warehouse === value,
      render: value => value ? `${stockByWarehouse.get(value)?.name || value} (${value})` : '—' },
    { title: 'Quantité', dataIndex: 'quantity', width: 100, align: 'right', sorter: (a, b) => a.quantity - b.quantity,
      render: (value, row) => <strong className={row.direction === 'entree' ? 'article-movement-entry' : 'article-movement-exit'}>{displayNumber(value, 2)}</strong> },
    { title: 'Stock actuel', key: 'stock', width: 190, render: (_, row) => {
      const stock = stockByWarehouse.get(row.warehouse);
      return stock ? stockTag(stock) : <Tag>N/A</Tag>;
    } },
  ];

  return <Card className="article-movements">
    <div className="article-movement-toolbar">
      <h2>Journal entrées/sorties</h2>
      <Space wrap>
        <Button onClick={() => { setFilters({}); setPagination(value => ({ ...value, current: 1 })); }}>Réinitialiser les filtres</Button>
        <Button icon={<ReloadOutlined />} loading={loading} onClick={() => setRevision(value => value + 1)}>Actualiser</Button>
      </Space>
    </div>
    {error ? <Alert type="error" showIcon message={error} action={<Button onClick={() => setRevision(value => value + 1)}>Réessayer</Button>} /> : <>
      <Space wrap className="article-movement-stocks" aria-label="Stocks actuels par entrepôt">
        <strong>Stock actuel :</strong>{visibleStocks.map(stockTag)}
      </Space>
      <Table aria-label="Mouvements de l’article" rowKey="id" size="small" loading={loading} columns={columns} dataSource={movements}
        scroll={{ x: 1315 }}
        locale={{ emptyText: 'Aucun mouvement pour cet article avec ces filtres.' }}
        pagination={{ ...pagination, showSizeChanger: true, pageSizeOptions: ['10', '20', '50'] }}
        onChange={(nextPagination, nextFilters, sorter, extra) => {
          setFilters(nextFilters);
          setPagination({ current: extra.action === 'filter' ? 1 : nextPagination.current, pageSize: nextPagination.pageSize });
        }} />
    </>}
  </Card>;
}
